import axios, {
  AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'

const baseURL = (window as any).pexitalk?.config?.API_BASE || import.meta.env.VITE_API_BASE

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 15000,
})

/**
 * Construit l'URL absolue d'un fichier média servi par le backend (ex. photo
 * de profil). Le backend renvoie un chemin relatif (`/media/avatars/…`) ; on
 * le préfixe avec la base API. Renvoie `null` si pas de chemin.
 */
export function mediaUrl(path?: string | null): string | null {
  if (!path) return null
  if (/^(https?:|data:|blob:)/.test(path)) return path
  // `baseURL` connu (Electron via preload, ou VITE_API_BASE) → URL absolue.
  // Sinon (build web servi par Caddy en MÊME origine, sans VITE_API_BASE) → on
  // renvoie le chemin RELATIF : le navigateur le résout contre l'origine
  // courante (ex. https://pexitalk.com/media/…), servie par Caddy. Ne JAMAIS
  // retomber sur localhost (injoignable depuis le navigateur en prod).
  return baseURL ? `${baseURL}${path}` : path
}

apiClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('pexitalk-jwt')
  if (token) cfg.headers.Authorization = `Bearer ${token}`
  return cfg
})

// ── Refresh JWT automatique ──────────────────────────────────────────────
// Quand un 401 tombe, on essaie de rejouer la requête une fois après avoir
// rafraîchi le token. Pour éviter une tempête de refresh en parallèle, on
// déduplique : tous les 401 simultanés attendent la même promesse.
let refreshPromise: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  const refresh = localStorage.getItem('pexitalk-refresh')
  if (!refresh) return null
  try {
    // axios direct (pas apiClient) pour éviter de réinjecter le token expiré.
    // `baseURL` vide (build web même origine, sans VITE_API_BASE) → URL RELATIVE
    // `/api/...` (résolue contre l'origine courante). NE PAS interpoler un
    // `baseURL` vide : `${undefined}/api/…` donnait « undefined/api/… » → le
    // refresh échouait → l'utilisateur était déconnecté ~1h après le login.
    const refreshUrl = baseURL
      ? `${baseURL}/api/auth/token/refresh/`
      : '/api/auth/token/refresh/'
    const resp = await axios.post(refreshUrl, { refresh }, { timeout: 10000 })
    const newAccess = resp.data?.access
    if (newAccess) {
      localStorage.setItem('pexitalk-jwt', newAccess)
      // Si la rotation des refresh est activée côté serveur, le refresh renvoie
      // aussi un NOUVEAU refresh token (l'ancien est blacklisté) : on DOIT le
      // persister, sinon le refresh suivant enverrait un token mort → 401. Par
      // défaut la rotation est désactivée (« session toujours connectée ») : pas
      // de `refresh` dans la réponse → on garde l'actuel. Le `if` couvre les deux.
      const newRefresh = resp.data?.refresh
      if (newRefresh) localStorage.setItem('pexitalk-refresh', newRefresh)
      return newAccess
    }
  } catch {
    // refresh expiré ou révoqué → on tombe sur le flow logout
  }
  return null
}

function redirectToLogin(): void {
  localStorage.removeItem('pexitalk-jwt')
  localStorage.removeItem('pexitalk-refresh')
  // Éviction de session / refresh mort : couper les timers d'auto-transition
  // (dont le heartbeat de CONNEXION inconditionnel), sinon ils continuent de
  // POSTer sans token → 401 → refresh échoué → redirectToLogin en boucle.
  // Import dynamique pour éviter un cycle client ↔ store agent_status.
  import('@/shared/stores/agent_status')
    .then(({ useAgentStatusStore }) => {
      try { useAgentStatusStore().stopAutoTransitions() } catch { /* pinia pas prêt */ }
    })
    .catch(() => { /* best-effort */ })
  window.location.hash = '/login'
}

apiClient.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config as InternalAxiosRequestConfig & { _retried?: boolean }
    const isRefreshCall = original?.url?.includes('/api/auth/token/refresh/')
    if (err.response?.status !== 401 || !original || original._retried || isRefreshCall) {
      if (err.response?.status === 401 && !isRefreshCall) {
        redirectToLogin()
      }
      return Promise.reject(err)
    }
    original._retried = true
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null
      })
    }
    const newToken = await refreshPromise
    if (!newToken) {
      redirectToLogin()
      return Promise.reject(err)
    }
    if (!original.headers) {
      original.headers = new AxiosHeaders()
    }
    original.headers.set('Authorization', `Bearer ${newToken}`)
    return apiClient(original)
  },
)
